import {
  Combobox as FluentCombobox,
  ComboboxProps as FluentComboboxProps,
} from '@fluentui/react-components';
import { forwardRef, useRef } from 'react';
import { useInputAppearance } from '../hooks';

export type ComboboxProps = FluentComboboxProps;

/**
 * Fluent's Combobox does not bring the currently selected option into view when
 * the listbox opens. Once the popover has rendered we scroll the listbox (only
 * the listbox, never the page) so the selected option sits as close as possible
 * to the combobox edge: at the top of the list when it opens downwards, at the
 * bottom when it opens upwards. When the option is near an end of the list it
 * lands as near as the available items allow (the browser clamps `scrollTop`).
 *
 * The selected option is located generically via `aria-selected`, so callers do
 * not have to wire a ref onto their `Option` elements.
 */
const scrollSelectedOptionIntoView = (input: HTMLInputElement | null) => {
  const option = document.querySelector<HTMLElement>('[role="listbox"] [aria-selected="true"]');
  const listbox = option?.closest<HTMLElement>('[role="listbox"]');
  if (!input || !option || !listbox) {
    return;
  }
  const optionRect = option.getBoundingClientRect();
  const listRect = listbox.getBoundingClientRect();
  const openedUpwards = listRect.top < input.getBoundingClientRect().top;
  listbox.scrollTop += openedUpwards
    ? optionRect.bottom - listRect.bottom
    : optionRect.top - listRect.top;
};

/**
 * Themed Combobox wrapper that:
 * - automatically applies the correct filled appearance based on the current
 *   theme (dark/light); the appearance can still be overridden via props, and
 * - scrolls the selected option into view when the listbox opens (see
 *   {@link scrollSelectedOptionIntoView}).
 *
 * Every other prop is forwarded unchanged and the ref points at the underlying
 * `<input>`.
 */
export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(
  ({ appearance, onOpenChange, ...props }, ref) => {
    const themedAppearance = useInputAppearance();
    const innerRef = useRef<HTMLInputElement | null>(null);

    const setRef = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const handleOpenChange: FluentComboboxProps['onOpenChange'] = (event, data) => {
      onOpenChange?.(event, data);
      if (!data.open) {
        return;
      }
      requestAnimationFrame(() => scrollSelectedOptionIntoView(innerRef.current));
    };

    return (
      <FluentCombobox
        ref={setRef}
        appearance={appearance ?? themedAppearance}
        onOpenChange={handleOpenChange}
        {...props}
      />
    );
  },
);

Combobox.displayName = 'Combobox';
